import axios from 'axios';
import { API_ENDPOINTS, API_CONFIG, corsProxiedUrl } from '../config/api';
import { remoteConfig, RemoteConfigKeys } from './remoteConfigService';
import { mleService } from './mleService';
import { clearSession } from './sessionService';

export const FARMVEST_API_CONFIG = {
    getBaseUrl: () => {
        return API_CONFIG.getFarmVestBaseUrl();
    },
    getApiKey: () => process.env.REACT_APP_FARMVEST_API_KEY || 'bWFya3dhdmUtZmFybXZlc3QtdGVzdHRpbmctYXBpa2V5',
    getBasicAuth: () => {
        const mode = API_CONFIG.getEnvironment();
        let username: string;
        let password: string;
        if (mode === 'live') {
            username = remoteConfig.getValue(RemoteConfigKeys.LIVE_USERNAME) || '';
            password = remoteConfig.getValue(RemoteConfigKeys.LIVE_PASSWORD) || '';
        } else {
            username = remoteConfig.getValue(RemoteConfigKeys.STAGING_USERNAME) || '';
            password = remoteConfig.getValue(RemoteConfigKeys.STAGING_PASSWORD) || '';
        }
        return 'Basic ' + btoa(`${username}:${password}`);
    }
};

export const farmvestApi = axios.create({
    baseURL: FARMVEST_API_CONFIG.getBaseUrl(),
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    }
});

// Dedicated axios instance for Odoo Inventory API
export const odooApi = axios.create({
    baseURL: API_CONFIG.getOdooBaseUrl(),
    headers: { 'Content-Type': 'application/json' },
});

odooApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('odoo_token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// Dedicated axios instance for AnimalKart API — Bearer token from Firebase Remote Config
export const animalKartApi = axios.create({
    headers: { 'Content-Type': 'application/json' },
});

animalKartApi.interceptors.request.use((config) => {
    config.baseURL = API_CONFIG.getBaseUrl();
    config.headers['Authorization'] = `Bearer ${API_CONFIG.getAnimalKartToken()}`;
    return config;
});

// Decode a JWT's `exp` claim (epoch seconds) without a library; 0 if unreadable.
function getTokenExpiry(token: string): number {
    try {
        const payload = token.split('.')[1];
        if (!payload) return 0;
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof decoded.exp === 'number' ? decoded.exp : 0;
    } catch {
        return 0;
    }
}

// How many seconds BEFORE the access token's real expiry we refresh it proactively.
// This is a refresh-lead window, NOT a clock-drift tolerance — it is unrelated to the
// MLE `clockSkewSeconds` used for encryption-envelope validation. 60 = refresh within the
// last 1 minute of the token's life; bump to 240 for a 4-minute window. Keep this
// comfortably below the access token's TTL, otherwise nearly every request will refresh.
const REFRESH_LEAD_SECONDS = 60;

// True when the token has a readable `exp` that is already past (or within the refresh-lead
// window). Tokens with no readable `exp` (e.g. a hand-corrupted token) return false so they
// fall through to the reactive 401 path instead of being refreshed blindly.
function isAccessTokenExpiring(token: string, leadSeconds = REFRESH_LEAD_SECONDS): boolean {
    const exp = getTokenExpiry(token);
    if (!exp) return false;
    return Date.now() / 1000 + leadSeconds >= exp;
}

// Add request interceptor to handle dynamic Authorization, farm_id injection, and MLE encryption
farmvestApi.interceptors.request.use(async (config) => {
    config.baseURL = FARMVEST_API_CONFIG.getBaseUrl();
    // A retried request (after a 401 -> token refresh) already had its body/params
    // encrypted on the first pass with the still-valid MLE keys. Only the Authorization
    // header needs the fresh token, so skip re-encryption to avoid double-encrypting.
    const isRetry = (config as any)._retry === true;
    const savedSession = localStorage.getItem('ak_dashboard_session');
    let token = null;
    let tokenType = 'Bearer';
    let farmId: string | null = null;
    let role = '';

    if (savedSession) {
        try {
            const session = JSON.parse(savedSession);
            if (session.access_token && session.access_token !== 'static_staging_token') {
                token = session.access_token;
                tokenType = session.token_type || 'Bearer';
            }
            farmId = session.farm_id || null;
            role = (session.role || '').toLowerCase();
        } catch (e) {
        }
    }

    // Proactive refresh: if the access token is expired (or about to be), exchange the
    // refresh_token for a fresh one BEFORE the request goes out. Relying solely on a server
    // 401 to trigger refresh is fragile — on a page reload the MLE guard below can log the
    // user out before any request reaches the server, so the 401-based refresh never fires.
    // Refreshing here guarantees expiry always triggers a refresh attempt first.
    const isAuthApiEarly = config.url?.includes('/auth/');
    if (!isRetry && !isAuthApiEarly && token && isAccessTokenExpiring(token)) {
        const refreshed = await performRefresh();
        if (refreshed) {
            token = refreshed;
            // Re-read token_type in case the refresh response rotated it.
            try {
                const fresh = JSON.parse(localStorage.getItem('ak_dashboard_session') || '{}');
                tokenType = fresh.token_type || tokenType;
            } catch { /* keep existing tokenType */ }
        }
    }

    if (token) {
        config.headers['Authorization'] = `${tokenType} ${token}`;
    } else {
        config.headers['Authorization'] = FARMVEST_API_CONFIG.getBasicAuth();
    }

    // Append farm_id to every request for non-superadmin users.
    // The animal draft endpoint is keyed by order + investor + buffalo, not farm —
    // it must NOT receive a farm_id param, so it is excluded here.
    const isSuperAdmin = role === 'super admin' || role === 'superadmin' || role === 'super_admin';
    const isFarmIdExcluded = (config.url || '').includes('/animal/draft');
    if (!isSuperAdmin && farmId && !isFarmIdExcluded) {
        config.params = { farm_id: farmId, ...config.params };
    }

    // MLE: encrypt POST / PUT / PATCH bodies once the key exchange is complete.
    // The login request itself is never encrypted (mleService.isReady() is false
    // until the server public key is received in the login response).
    const method = (config.method ?? '').toLowerCase();
    const isMleEnabled = localStorage.getItem('mle_enabled') === 'true';
    const isAuthApi = config.url?.includes('/auth/');

    // If MLE is required but the keys are lost in memory (e.g., page refresh), 
    // attempt to restore them from IndexedDB. If restoration fails, force re-login.
    // We cannot send plain text when the server expects encrypted data.
    // Skip this check for /auth/ APIs since MLE cannot be ready until login completes.
    if (!isAuthApi && isMleEnabled && !mleService.isReady()) {
        const restored = await mleService.restoreSession();
        if (!restored) {
            console.warn('[MLE] MLE is enabled but keys could not be restored from IndexedDB. Forcing re-login.');
            await clearSession();
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
            return Promise.reject(new Error('MLE session expired. Please log in again.'));
        }
    }

    if (!isRetry && mleService.isReady() && ['post', 'put', 'patch'].includes(method)) {
        // Extract any inline query string from the URL (e.g. ?farm_id=...) so it gets encrypted too.
        let inlinePostParams: Record<string, string> = {};
        if (config.url && config.url.includes('?')) {
            const [baseUrl, qs] = config.url.split('?', 2);
            config.url = baseUrl;
            const searchParams = new URLSearchParams(qs);
            searchParams.forEach((value, key) => { inlinePostParams[key] = value; });
        }

        // Merge: body data + inline params + config.params (e.g. farm_id injected earlier).
        // This mirrors GET behaviour — everything goes into the encrypted payload, nothing leaks in plain text.
        let bodyData: Record<string, unknown> = {};
        if (config.data != null) {
            if (typeof config.data === 'string') {
                try {
                    bodyData = JSON.parse(config.data);
                } catch {
                    console.warn('[MLE] Non-JSON string body — skipping body merge into encrypted payload');
                    bodyData = {};
                }
            } else {
                bodyData = config.data;
            }
        }
        const mergedPayload: Record<string, unknown> = {
            ...inlinePostParams,
            ...(config.params ?? {}),
            ...bodyData,  // body fields win on collision
        };

        try {
            const encrypted = await mleService.encryptRequest(mergedPayload);
            config.data = encrypted;
            config.params = {};  // clear so nothing leaks as a plain query string
            console.log('[MLE] POST/PUT/PATCH encrypted — params merged into body and cleared');
        } catch (encErr) {
            console.warn('[MLE] POST/PUT/PATCH encryption failed, sending plain:', encErr);
            config.data = mergedPayload;
            config.params = {};
        }
    }

    // MLE: encrypt GET query params once the key exchange is complete.
    // MOU document endpoints return HTML/PDF (non-JSON) — backend bypasses MLE for them,
    // so we must NOT encrypt their params.
    const isMouDocEndpoint = config.url ? (config.url.includes('/mou/html') || config.url.includes('/mou/pdf')) : false;
    console.log('[MLE] isReady:', mleService.isReady(), '| method:', method);
    if (!isRetry && mleService.isReady() && method === 'get' && !isMouDocEndpoint) {
        // 1. Extract and strip inline query string from the URL (if any).
        let inlineParams: Record<string, string> = {};
        if (config.url && config.url.includes('?')) {
            const [baseUrl, qs] = config.url.split('?', 2);
            config.url = baseUrl;
            const searchParams = new URLSearchParams(qs);
            searchParams.forEach((value, key) => { inlineParams[key] = value; });
        }

        // 2. Merge: inline params + config.params (config.params wins on collision).
        const allParams: Record<string, unknown> = { ...inlineParams, ...(config.params ?? {}) };

        // 3. Only encrypt if there is at least one param.
        if (Object.keys(allParams).length > 0) {
            try {
                const encrypted = await mleService.encryptRequest(allParams);
                config.params = encrypted;
            } catch (encErr) {
                console.warn('[MLE] GET encryption failed, sending plain:', encErr);
                config.params = allParams;
            }
        }
    }

    console.log('[MLE] final config url:', config.url, '| params:', config.params);
    return config;
}, (error) => {
    return Promise.reject(error);
});

// MLE error keywords that indicate the server could not verify the client's signature.
const MLE_ERROR_KEYWORDS = [
    'Signature verification',
    'not encrypted',
    'MLE enabled',
    'invalid client key',
    'Decryption/Verification failed',
] as const;

/**
 * Clear all session/MLE state and redirect to login.
 * keysToRemove controls which localStorage keys are purged in addition to the
 * MLE state (which is always cleared).
 */
function forceRelogin(keysToRemove: string[]): void {
    mleService.clearKeys();
    keysToRemove.forEach(k => localStorage.removeItem(k));
    if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
    }
}

// --- Refresh-token handling -------------------------------------------------
// When the access token expires the server replies 401. We exchange the stored
// refresh_token for a fresh access_token (+ rotated refresh_token), persist it,
// and replay the failed request. A single-flight guard ensures that if many
// requests 401 at once we only call /api/auth/refresh once and queue the rest.
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

function onTokenRefreshed(token: string | null): void {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
}

function subscribeTokenRefresh(cb: (token: string | null) => void): void {
    refreshSubscribers.push(cb);
}

/**
 * Exchange the stored refresh_token for a new access_token.
 * Uses a bare axios call (not farmvestApi) so it bypasses the request/response
 * interceptors — no MLE encryption, no Bearer header, and no risk of the refresh
 * call itself triggering another refresh on failure. The refresh endpoint expects
 * Basic auth (same as pre-login) and a plain JSON body, mirroring the backend curl.
 * Returns the new access token, or null if refresh is impossible/failed.
 */
async function refreshAccessToken(): Promise<string | null> {
    const savedSession = localStorage.getItem('ak_dashboard_session');
    if (!savedSession) return null;

    let session: any;
    try {
        session = JSON.parse(savedSession);
    } catch {
        return null;
    }

    const refreshToken = session?.refresh_token;
    if (!refreshToken) return null;

    try {
        const resp = await axios.post(
            API_ENDPOINTS.refreshToken(),
            { refresh_token: refreshToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json',
                    'Authorization': FARMVEST_API_CONFIG.getBasicAuth(),
                },
            }
        );

        const newAccessToken = resp.data?.access_token;
        if (!newAccessToken) return null;

        // The refresh token rotates on every call — persist the new pair.
        session.access_token = newAccessToken;
        if (resp.data?.refresh_token) session.refresh_token = resp.data.refresh_token;
        if (resp.data?.token_type) session.token_type = resp.data.token_type;
        localStorage.setItem('ak_dashboard_session', JSON.stringify(session));

        return newAccessToken;
    } catch (err) {
        console.warn('[Auth] Token refresh request failed:', err);
        return null;
    }
}

/**
 * Single-flight wrapper around refreshAccessToken(). Shared by the proactive
 * request-interceptor path and the reactive 401 response-interceptor path so that
 * concurrent callers only trigger ONE /api/auth/refresh call and all receive its result.
 */
async function performRefresh(): Promise<string | null> {
    if (isRefreshing) {
        return new Promise((resolve) => subscribeTokenRefresh(resolve));
    }
    isRefreshing = true;
    const newToken = await refreshAccessToken();
    isRefreshing = false;
    onTokenRefreshed(newToken);
    return newToken;
}

// Add response interceptor to handle MLE decryption and global errors (like 401 Unauthorized)
farmvestApi.interceptors.response.use(
    async (response) => {
        // MLE: if the server returned an encrypted response, decrypt and verify it.
        console.log('[MLE] Response interceptor - isReady:', mleService.isReady(), 'data type:', typeof response.data);
        if (typeof response.data === 'string' && response.data.trim().startsWith('eyJ') && response.data.trim().split('.').length === 5 && mleService.isReady()) {
            console.log('[MLE] Detected raw JWE string response, decrypting...');
            try {
                response.data = await mleService.decryptResponse(response.data.trim());
                console.log('[MLE] Successfully decrypted response data');
            } catch (err) {
                console.error('[MLE] Failed to decrypt response data:', err);
                return Promise.reject(err);
            }
        } else if (response.data?.secure_response && mleService.isReady()) {
            console.log('[MLE] Detected secure_response field, decrypting...');
            try {
                response.data = await mleService.decryptResponse(response.data.secure_response);
                console.log('[MLE] Successfully decrypted secure_response data');
            } catch (err) {
                console.error('[MLE] Failed to decrypt secure_response data:', err);
                return Promise.reject(err);
            }
        }
        return response;
    },
    async (error) => {
        // MLE: If the backend returns an encrypted error response (e.g., 400), decrypt it so the frontend can read the actual error message.
        if (error.response && error.response.data) {
            if (typeof error.response.data === 'string' && error.response.data.trim().startsWith('eyJ') && error.response.data.trim().split('.').length === 5 && mleService.isReady()) {
                try {
                    error.response.data = await mleService.decryptResponse(error.response.data.trim());
                    console.log('[MLE] Successfully decrypted error response data');
                } catch (err) {
                    console.error('[MLE] Failed to decrypt error response data:', err);
                }
            } else if (error.response.data.secure_response && mleService.isReady()) {
                try {
                    error.response.data = await mleService.decryptResponse(error.response.data.secure_response);
                    console.log('[MLE] Successfully decrypted error secure_response data');
                } catch (err) {
                    console.error('[MLE] Failed to decrypt error secure_response data:', err);
                }
            }
        }

        const originalRequest = error.config;
        const isAuthRequest = originalRequest?.url?.includes('/auth/');

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
            // Another request is already refreshing — queue this one until it finishes.
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    subscribeTokenRefresh((token) => {
                        if (token) {
                            originalRequest._retry = true;
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(farmvestApi(originalRequest));
                        } else {
                            reject(error);
                        }
                    });
                });
            }

            originalRequest._retry = true;
            const newToken = await performRefresh();

            if (newToken) {
                // Replay the original request. The request interceptor re-reads the
                // updated session and attaches the new token; _retry skips re-encryption.
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return farmvestApi(originalRequest);
            }

            // Refresh failed (no/expired refresh_token) — log out and send to login.
            console.warn('Token refresh failed. Clearing session...');
            await clearSession();
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }

        // MLE key mismatch — server could not verify our signature (keys diverged, e.g. another
        // login session overwrote the stored public key).  Clearing stale keys and forcing
        // re-login re-establishes a valid key pair in the DB and breaks the cascade where every
        // subsequent encrypted request fails with the same error.
        if (error.response?.status === 400 && mleService.isReady()) {
            const detail: string = error.response.data?.detail ?? error.response.data?.message ?? '';
            if (MLE_ERROR_KEYWORDS.some(kw => detail.includes(kw))) {
                console.warn('[MLE] Key mismatch detected (400). Clearing MLE state and forcing re-login.');
                forceRelogin(['mle_enabled', 'ak_dashboard_session']);
            }
        }


        return Promise.reject(error);
    }
);

// Response interceptor for AnimalKart API — mirrors farmvestApi 401 handling
animalKartApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('ak_dashboard_session');
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Helper to get the admin mobile for X-Admin-Mobile header.
 * Replaces hardcoded values/sessions as requested by USER.
 */
const getAdminMobile = async (override?: string): Promise<string> => {
    // Check cached value first (to avoid repetitive API calls)
    const cachedAdminMobile = sessionStorage.getItem('dynamic_admin_mobile');
    if (!override && cachedAdminMobile) {
        return cachedAdminMobile;
    }

    const sessionStr = localStorage.getItem('ak_dashboard_session');
    let sessionMobile = '';
    if (sessionStr) {
        try {
            const session = JSON.parse(sessionStr);
            sessionMobile = session.mobile || '';
        } catch (e) { }
    }
    const mobile = override || sessionMobile || localStorage.getItem('ak_dashboard_last_mobile') || '';

    // Dynamically take from the API "/users/admins/list" and take the first value
    if (!override) {
        try {
            const response = await animalKartApi.get(API_ENDPOINTS.getAdminsList(), {
                headers: {
                    'X-Admin-Mobile': mobile, // pass original as fallback for auth
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            const data = response.data;

            let users = [];
            if (data && Array.isArray(data.users)) {
                users = data.users;
            } else if (data && data.data && Array.isArray(data.data.users)) {
                users = data.data.users;
            }

            if (users.length > 0 && users[0].mobile) {
                const dynamicMobile = String(users[0].mobile);
                sessionStorage.setItem('dynamic_admin_mobile', dynamicMobile);
                return dynamicMobile;
            }
        } catch (e) {
            console.error('Error fetching dynamic admin list for X-Admin-Mobile:', e);
        }
    }

    return mobile;
};

export const farmvestService = {
    staticLogin: async (mobile_number: string, otp: string) => {
        try {
            // This call will usage the API Key via the interceptor (since no token exists yet)
            const response = await farmvestApi.post(API_ENDPOINTS.staticLogin(), {
                mobile_number,
                otp
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    loginWithOtp: async (mobile_number: string, otp: string) => {
        try {
            // Include the client public key so the server can verify our signed requests.
            // This is plain JSON — MLE is not active yet (no server public key yet).
            const clientPublicKeyPem = mleService.getPublicKeyPem();
            const payload: Record<string, unknown> = { mobile_number, otp };
            if (clientPublicKeyPem) {
                payload.client_public_key = clientPublicKeyPem;
            }

            const response = await farmvestApi.post(API_ENDPOINTS.loginWithOtp(), payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                }
            });

            // Store the server's public key so MLE becomes active for all subsequent requests.
            // Explicitly verify the server tells us MLE is enabled via response.data.mle_enabled.
            const isMleEnabledOnServer = response.data?.mle_enabled === true || response.data?.mle_enabled === 'true';
            localStorage.setItem('mle_enabled', String(isMleEnabledOnServer));

            if (isMleEnabledOnServer && response.data?.server_public_key) {
                await mleService.setServerPublicKey(response.data.server_public_key);
            }

            return response.data;
        } catch (error) {
            throw error;
        }
    },
    adminLogin: async (mobile_number: string, adminKey: string) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.adminLogin(), { mobile_number }, {
                headers: { 'X-Admin-Key': adminKey }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getUserData: async (mobile: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getFarmVestUserData()}?mobile=${mobile}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getEmployees: async (params?: { role?: string; is_active?: number; sort_by?: number; page?: number; size?: number; farm_id?: number; search?: string }) => {
        try {
            const { role, is_active: active_status, sort_by = 1, page = 1, size = 20, farm_id } = params || {};
            let query = `?sort_by=${sort_by}&page=${page}&size=${size}`;
            if (role) query += `&role=${role}`;
            if (active_status !== undefined && active_status !== null && active_status.toString() !== '') query += `&is_active=${active_status}`;
            if (farm_id) query += `&farm_id=${farm_id}`;
            if (params?.search) query += `&search=${encodeURIComponent(params.search)}`;

            const url = `${API_ENDPOINTS.getAllEmployees()}${query}`;
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    // Employee data export — GET /api/employee/export?year=&month= (both optional; omit for all data).
    // Returns the full axios response so callers can read content-type/content-disposition
    // and handle either a file (blob) or a JSON payload.
    exportEmployeeData: async (year?: number, month?: number) => {
        return farmvestApi.get(API_ENDPOINTS.exportEmployeeData(year, month), { responseType: 'blob' });
    },
    getAllInvestors: async (params?: { page?: number; size?: number; is_active?: boolean; search?: string; cpf_pending?: boolean; is_onboarded?: boolean }) => {
        try {
            const { page = 1, size = 10000, is_active, search, cpf_pending, is_onboarded } = params || {};
            const baseUrl = API_ENDPOINTS.getAllInvestors();
            let url = `${baseUrl}?page=${page}&size=${size}`;

            if (is_active !== undefined) {
                url += `&is_active=${is_active}`;
            }

            if (cpf_pending !== undefined) {
                url += `&cpf_pending=${cpf_pending}`;
            }

            if (is_onboarded !== undefined) {
                url += `&is_onboarded=${is_onboarded}`;
            }

            if (search) {
                url += `&search=${encodeURIComponent(search)}`;
            }

            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getInvestorProfileByMobile: async (mobile: string): Promise<{
        found: boolean;
        nominee_details: { name: string; mobile: string; relation: string; aadhar_card: string; pancard_number: string; pan_card_url?: string; pan_number?: string; pan_url?: string; pancard_photo?: string } | null;
        revenue_account_details: { account_holder_name: string; account_number: string; ifsc_code: string; bank_image_url: string; image_url?: string } | null;
        profile: any | null;
    }> => {
        const response = await farmvestApi.get(
            `${API_ENDPOINTS.getAllInvestors()}?search=${encodeURIComponent(mobile)}&size=1`
        );
        const investor = response.data?.data?.[0];
        if (!investor) return { found: false, nominee_details: null, revenue_account_details: null, profile: null };
        return {
            found: true,
            nominee_details: investor.nominee_details ?? null,
            revenue_account_details: investor.revenue_account_details ?? null,
            profile: investor
        };
    },

    getAdminSummary: async (farm_id?: number, shed_id?: number, month?: number, year?: number) => {
        let url = API_ENDPOINTS.getAdminSummary();
        const params = new URLSearchParams();
        if (farm_id) params.append('farm_id', farm_id.toString());
        if (shed_id) params.append('shed_id', shed_id.toString());
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());
        const queryString = params.toString();
        const response = await farmvestApi.get(queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url);
        return response.data;
    },
    getInvestorSummary: async () => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.getInvestorSummary());
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getInvestorMOUHtml: async (investorId: number | string) => {
        try {
            const response = await farmvestApi.get(`/api/investors/${investorId}/mou/html`);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    downloadInvestorMOUPdf: async (investorId: number | string) => {
        try {
            const response = await farmvestApi.get(`/api/investors/${investorId}/mou/pdf`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `FarmVest_MOU_${investorId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error: any) {
            throw error;
        }
    },
    getFarms: async (location: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getAllFarms()}?location=${location}`);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getLocations: async (params?: { page?: number; size?: number }) => {
        try {
            let url = API_ENDPOINTS.getFarmLocations();
            if (params) {
                const query = new URLSearchParams();
                if (params.page) query.append('page', params.page.toString());
                if (params.size) query.append('size', params.size.toString());
                url += `?${query.toString()}`;
            }
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getLocationHierarchy: async () => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.createLocation());
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    createLocation: async (locationData: { name: string; prefix: string }) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.createLocation(), locationData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getAllFarms: async (params?: { location?: string, location_name?: string, sort_by?: number, page?: number, size?: number, search?: string, search_query?: string, name?: string }) => {
        try {
            let url = API_ENDPOINTS.getAllFarms(); // GET /api/farm/get_all_farms
            if (params) {
                const query = new URLSearchParams();
                if (params.location) query.append('location', params.location);
                if (params.location_name) query.append('location_name', params.location_name);
                if (params.sort_by) query.append('sort_by', params.sort_by.toString());
                if (params.page) query.append('page', params.page.toString());
                if (params.size) query.append('size', params.size.toString());
                if (params.search) query.append('search', params.search);
                if (params.search_query) query.append('search_query', params.search_query);
                if (params.name) query.append('name', params.name);
                url += `?${query.toString()}`;
            }
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    searchEmployee: async (query: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.searchEmployee()}?search_query=${query}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getAdminsList: async (adminMobileOverride?: string) => {
        try {
            const adminMobile = await getAdminMobile(adminMobileOverride);

            const response = await animalKartApi.get(API_ENDPOINTS.getAdminsList(), {
                headers: {
                    'X-Admin-Mobile': adminMobile,
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    updateOrderStatus: async (payload: {
        orderId: string;
        status: string;
        buffaloId: string;
        buffaloIds: string[];
        description: string;
        location: string;
    }, adminMobileOverride?: string) => {
        const adminMobile = await getAdminMobile(adminMobileOverride);

        // Transform payload to match new procurement action schema
        const newPayload = {
            action: payload.status || "mark_onboarded", // Use status as action
            buffaloIds: payload.buffaloIds || (payload.buffaloId ? [payload.buffaloId] : []),
            sendNotification: true
        };

        // Explicitly include X-Admin-Mobile and X-Requested-With
        const response = await animalKartApi.post(API_ENDPOINTS.updateOrderStatus(), newPayload, {
            headers: {
                'X-Admin-Mobile': adminMobile,
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    },
    updateOrderMarketplaceStatus: async (orderId: string, statusData: any, adminMobileOverride?: string) => {
        const adminMobile = await getAdminMobile(adminMobileOverride);

        // Transform statusData to match new procurement action schema
        const payload = {
            action: statusData.action || (statusData.stage === "Onboarded" ? "mark_onboarded" : statusData.stage) || "mark_onboarded",
            orderId: orderId.replace(/^OD#/, ''),
            buffaloIds: statusData.buffalo_ids || statusData.buffaloIds || [],
            sendNotification: true
        };

        const response = await animalKartApi.post(API_ENDPOINTS.updateOrderStatus(), payload, {
            headers: {
                'X-Admin-Mobile': adminMobile,
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    },
    getOrderTrackingStages: async (orderId: string, adminMobileOverride?: string) => {
        try {
            const adminMobile = await getAdminMobile(adminMobileOverride);

            // Note: Backend requires POST for this endpoint and expects orderId in body
            const response = await animalKartApi.post(API_ENDPOINTS.getOrderStatus(), {
                orderId: orderId
            }, {
                headers: {
                    'X-Admin-Mobile': adminMobile,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    createEmployee: async (employeeData: any) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.createEmployee(), employeeData);
            const data = response.data;

            // Sync screen access of the assigned role
            const newUserId = data?.data?.id || data?.data?.user_id || data?.user_id || data?.id || data?.user?.id;
            const roles = employeeData.roles || [];
            const primaryRole = roles[0];

            if (newUserId && primaryRole) {
                try {
                    // Fetch all roles to find the matching one
                    const rolesRes = await farmvestApi.get('/api/permissions/roles/all');
                    const rolesList = rolesRes.data?.roles || [];
                    const roleTemplate = rolesList.find((r: any) => r.name.toLowerCase() === primaryRole.toLowerCase());

                    if (roleTemplate && roleTemplate.screen_access) {
                        const apiScreenAccess: Record<string, string> = {};
                        Object.entries(roleTemplate.screen_access).forEach(([k, v]) => {
                            apiScreenAccess[k.toLowerCase()] = String(v);
                        });
                        await farmvestApi.put(`/api/employee/screen_access/${newUserId}`, apiScreenAccess);
                    }
                } catch (roleErr) {
                    console.error('Failed to sync screen access for new employee:', roleErr);
                }
            }

            return data;
        } catch (error) {
            throw error;
        }
    },
    getEmployeeDetailsById: async (id: string) => {
        try {
            // Updated parameter name to 'user_id' as per Swagger documentation
            const response = await farmvestApi.get(`${API_ENDPOINTS.getEmployeeDetailsById()}?user_id=${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    deleteEmployee: async (id: number) => {
        try {
            const response = await farmvestApi.delete(API_ENDPOINTS.deleteEmployee(id));
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getAvailableSheds: async (farm_id: number, page: number = 1, size: number = 20) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.listSheds()}?farm_id=${farm_id}&page=${page}&size=${size}`);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getShedsByFarm: async (farmId: number) => {
        try {
            const url = `${API_ENDPOINTS.listSheds()}?farm_id=${farmId}&size=1000`;
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getShedList: async (farmId: number, page?: number, size?: number, shedId?: number) => {
        try {
            let url = `${API_ENDPOINTS.listSheds()}?farm_id=${farmId}`;
            if (page) url += `&page=${page}`;
            if (size) url += `&size=${size}`;
            if (shedId) url += `&shed_id=${shedId}`;
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getShedDetails: async (shedId: number) => {
        try {
            const response = await farmvestApi.get(`${FARMVEST_API_CONFIG.getBaseUrl()}/api/shed/details?shed_id=${shedId}`);
            return response.data;
        } catch (error: any) {
            return null;
        }
    },
    getShedPositions: async (shedId: number) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getAvailablePositionsInShed()}?shed_id=${shedId}`);
            return response.data;
        } catch (error: any) {
            return {
                status: "success",
                data: []
            };
        }
    },
    getAnimalPositions: async (shedId: number) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getAnimalByPosition()}?shed_id=${shedId}`);
            return response.data;
        } catch (error: any) {
            return {
                status: "success",
                data: []
            };
        }
    },
    getAnimalPositionDetails: async (params: { parkingId: string; farmId?: number; shedId?: number; rowNumber?: string }) => {
        try {
            const { parkingId, farmId, shedId, rowNumber } = params;
            let url = `${API_ENDPOINTS.getAnimalByPosition()}?parking_id=${parkingId}`;
            if (farmId) url += `&farm_id=${farmId}`;
            if (shedId) url += `&shed_id=${shedId}`;
            if (rowNumber) url += `&row_number=${rowNumber}`;

            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getTotalAnimals: async (
        farmId?: number | string,
        shedId?: number | string,
        page: number = 1,
        size: number = 15,
        investorId?: number | string,
        locationName?: string,
        locationId?: number | string,
        search?: string,
        healthStatus?: string,
        animalType?: string,
        pregnancyStatus?: string,
        animalStatus?: string,
        fetchRosterAnimals?: boolean | string,
        assignedStatus?: string,
        byPregnant?: string,
        ticketPriority?: string,
        isAiSuccessful?: boolean | string,
        onboardStatus?: string
    ) => {
        try {
            let url = API_ENDPOINTS.getTotalAnimals();
            const params = new URLSearchParams();
            if (farmId) params.append('farm_id', farmId.toString());
            if (shedId) params.append('shed_id', shedId.toString());
            if (investorId) params.append('investor_id', investorId.toString());
            if (locationName) params.append('location_name', locationName);
            if (locationId !== undefined && locationId !== null && locationId !== '') params.append('location_id', locationId.toString());
            if (search) params.append('search', search);
            if (healthStatus && healthStatus !== 'All') params.append('health_status', healthStatus.toUpperCase().replace(/\s+/g, '_'));
            // Default to buffalo so calves are excluded from the response unless a caller
            // explicitly requests another animal type (e.g. the Calf tab passes 'CALF').
            if (animalType !== 'ALL') {
                params.append('animal_type', animalType || 'BUFFALO');
            }

            // Map selected animal status or fallback pregnancy status to the status query parameter
            if (animalStatus && animalStatus !== 'All') {
                params.append('status', animalStatus);
                params.append('animal_status', animalStatus);
            } else if (pregnancyStatus) {
                params.append('status', pregnancyStatus);
            }

            // New filters requested by user
            if (fetchRosterAnimals !== undefined && fetchRosterAnimals !== '') params.append('roster_stage', fetchRosterAnimals.toString());
            if (assignedStatus && assignedStatus !== 'All') {
                params.append('assigned_status', assignedStatus);
                params.append('is_allocated', (assignedStatus.toLowerCase() === 'assigned' || assignedStatus.toLowerCase() === 'allocated' || assignedStatus.toLowerCase() === 'true') ? 'true' : 'false');
            }
            if (byPregnant && byPregnant !== 'All') {
                const isPregnantVal = (byPregnant === 'Pregnant' || byPregnant === 'true') ? 'true' : 'false';
                params.append('pregnant', isPregnantVal);
            }
            if (ticketPriority && ticketPriority !== 'All') params.append('ticket_priority', ticketPriority.toUpperCase());
            if (isAiSuccessful !== undefined && isAiSuccessful !== 'All' && isAiSuccessful !== '') params.append('is_ai_successful', isAiSuccessful.toString());
            if (onboardStatus && onboardStatus !== 'All') params.append('onboard_status', onboardStatus);

            params.append('page', page.toString());
            params.append('size', size.toString());

            if (params.toString()) url += `?${params.toString()}`;

            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getShedAllocation: async (shedId: string) => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.allocateAnimal(shedId));
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getUnallocatedAnimals: async (farmId: number, shedId?: string | number, page?: number, size?: number) => {
        try {
            const params = new URLSearchParams({ farm_id: String(farmId) });
            if (shedId !== undefined && shedId !== null && shedId !== '') params.set('shed_id', String(shedId));
            if (page !== undefined) params.set('page', String(page));
            if (size !== undefined) params.set('size', String(size));
            const url = `${API_ENDPOINTS.getUnallocatedAnimals()}?${params.toString()}`;
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getPendingOnboarding: async (page?: number, size?: number) => {
        try {
            let url = API_ENDPOINTS.getPendingOnboarding();
            if (page || size) {
                const params = new URLSearchParams();
                if (page) params.append('page', page.toString());
                if (size) params.append('size', size.toString());
                url += `?${params.toString()}`;
            }
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    quarantineLookup: async (buffaloId: string, orderId?: string) => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.quarantineLookup(buffaloId, orderId));
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    // Assign belt (neckband) and tag number to quarantine buffaloes
    quarantineAssign: async (payload: {
        animalkart_order_id: string;
        farm_id: number;
        is_from_local_quarantine?: boolean;
        buffaloes: Array<{
            animalkart_buffalo_id: string;
            neckband_id: string;
            tag_number: number;
        }>;
    }) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.quarantineAssign(), payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    // Save Animal Onboarding Draft
    saveAnimalDraft: async (payload: {
        draft_data?: Record<string, any>;
        current_step: number;
        is_transfer: boolean;
        investor_mobile: string;
        animalkart_order_id: string;
        animalkart_buffalo_id?: string;
    }) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.saveAnimalDraft(), payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    // Fetch a previously saved Animal Onboarding Draft
    getAnimalDraft: async (animalkart_order_id: string, investor_mobile: string, animalkart_buffalo_id?: string) => {
        try {
            const response = await farmvestApi.get(
                API_ENDPOINTS.getAnimalDraft(animalkart_order_id, investor_mobile, animalkart_buffalo_id)
            );
            return response.data;
        } catch (error: any) {
            // 404 means no draft exists — return null so callers can branch gracefully
            if (error?.response?.status === 404) return null;
            throw error;
        }
    },
    // Update animal details
    updateAnimal: async (animal_id: string | number, payload: {
        rfid_tag?: string;
        ear_tag?: string;
        neckband_id?: string;
        weight?: number;
        body_temperature?: number;
        health_status?: string;
        status?: string;
        dob?: string;
        images?: string[];
        video_url?: string;
        age_months?: number;
        rfid?: string;
        age?: number;
        ageMonths?: number;
    }, farm_id?: string | number) => {
        try {
            let url = API_ENDPOINTS.updateAnimal(animal_id);
            if (farm_id) url += `?farm_id=${farm_id}`;
            const response = await farmvestApi.patch(url, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    // Create Farm
    createFarm: (farmData: {
        farm_name?: string;
        location_name: string;
        location_prefix?: string;
        shed_count: number;
        is_test: boolean;
        address?: string;
        latitude?: number;
        longitude?: number;
        rows_per_shed?: number;
        shed_capacity?: number;
        slots_per_row?: number;
        quarantine_sheds?: number;
        quarantine_rows_per_shed?: number;
        quarantine_shed_capacity?: number;
        quarantine_slots_per_row?: number;
        calves_sheds?: number;
        calves_rows_per_shed?: number;
        calves_slots_per_row?: number;
        calves_shed_capacity?: number;
    }) => {
        return farmvestApi.post(API_ENDPOINTS.createFarm(), farmData);
    },
    updateFarm: async (farmId: number, farmData: {
        farm_name?: string;
        location_name?: string;
        shed_count?: number;
        is_test?: boolean;
        shed_capacity?: number;
        rows_per_shed?: number;
        slots_per_row?: number;
        quarantine_sheds?: number;
        quarantine_rows_per_shed?: number;
        quarantine_slots_per_row?: number;
        sheds?: { shed_id?: number; capacity: number; shed_name: string; rows_per_shed?: number; slots_per_row?: number }[];
    }) => {
        try {
            const response = await farmvestApi.put(API_ENDPOINTS.updateFarm(farmId), farmData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    deleteFarm: async (farmId: number) => {
        try {
            const response = await farmvestApi.delete(API_ENDPOINTS.deleteFarm(farmId));
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    createShed: async (shedData: { farm_id: number; shed_id?: string; shed_name: string; capacity: number; cctv_url?: string; is_quarantine?: boolean }) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.createShed(), shedData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    updateShed: async (shedId: number, data: {
        shed_name?: string;
        cctv_url?: string;
        camera_config?: Record<string, string[]>;
        is_active?: boolean;
        is_quarantine?: boolean;
        rows_per_shed?: number;
        slots_per_row?: number;
        polygon?: any;
    }) => {
        const response = await farmvestApi.put(`/api/shed/update_shed/${shedId}`, data);
        return response.data;
    },
    deactivateUser: async (mobile: string, remarks?: string, role?: string) => {
        try {
            const response = await farmvestApi.put(API_ENDPOINTS.activateDeactivateUser(mobile), {
                is_active: false,
                ...(role && { role_to_deactivate: role }),
                remarks: remarks || ""
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    activateUser: async (mobile: string, remarks?: string, role?: string) => {
        try {
            const response = await farmvestApi.put(API_ENDPOINTS.activateDeactivateUser(mobile), {
                is_active: true,
                ...(role && { role_to_activate: role }),
                remarks: remarks || ""
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    toggleRoleStatus: async (mobile: string, role: string, is_active: boolean, remarks?: string) => {
        try {
            const payload: Record<string, any> = {
                is_active,
                remarks: remarks || "",
            };
            if (is_active) {
                payload.role_to_activate = role;
            } else {
                payload.role_to_deactivate = role;
            }
            const response = await farmvestApi.put(API_ENDPOINTS.activateDeactivateUser(mobile), payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getPaidOrders: async (mobile: string, page: number = 1, size: number = 500, date?: string, cpfFilter?: string, deliveredOverdue?: string) => {
        try {
            const adminMobile = await getAdminMobile();

            let url = API_ENDPOINTS.getProcurementRoster();

            // Append query params since some endpoints ignore body for pagination
            const query = new URLSearchParams();
            query.append('page', page.toString());
            query.append('size', size.toString());

            if (date) {
                query.append('date', date);
            }

            if (cpfFilter !== undefined && cpfFilter !== null && cpfFilter !== "") {
                query.append('cpf_filter', cpfFilter);
            }

            if (deliveredOverdue !== undefined && deliveredOverdue !== null && deliveredOverdue !== "") {
                query.append('delivered_overdue', deliveredOverdue);
            }

            if (mobile && mobile.trim() !== "") {
                query.append('mobile', mobile);
            }

            url += `${url.includes('?') ? '&' : '?'}${query.toString()}`;

            const response = await animalKartApi.get(url, {
                headers: {
                    'X-Admin-Mobile': adminMobile
                }
            });

            if (response.status === 200 || response.status === 201) {
                return response.data; // Return the full { user, orders, status, statuscode }
            }
            return { user: null, orders: [] };
        } catch (error: any) {
            return { user: null, orders: [] };
        }
    },
    getInTransitOrders: async (mobile: string, filterStatus: string = 'delivered') => {
        try {
            const response = await animalKartApi.post(API_ENDPOINTS.getInTransitOrders(), {
                mobile,
                filter_status: filterStatus
            });
            return response.data;
        } catch (error) {
            console.error("In-transit orders API failed:", error);
            throw error;
        }
    },
    getInvestorDetailsByOrder: async (orderId: string) => {
        try {
            const response = await animalKartApi.get(API_ENDPOINTS.getInvestorDetailsByOrder(orderId));
            const raw = response.data;
            if (typeof raw === 'string') {
                try {
                    return JSON.parse(raw);
                } catch (e) {
                    return raw;
                }
            }
            return raw;
        } catch (error) {
            throw error;
        }
    },
    sendWhatsappOtp: async (mobile: string) => {
        const url = `${API_ENDPOINTS.sendWhatsappOtp()}?method=whatsapp`;
        const response = await farmvestApi.post(url, { mobile }, {
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    },

    sendEmailOtp: async (email: string) => {
        const url = `${API_ENDPOINTS.sendWhatsappOtp()}?method=email`;
        const response = await farmvestApi.post(url, { email }, {
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    },

    loginWithEmailOtp: async (email: string, otp: string) => {
        const clientPublicKeyPem = mleService.getPublicKeyPem();
        const payload: Record<string, unknown> = { email, otp };
        if (clientPublicKeyPem) payload.client_public_key = clientPublicKeyPem;

        const response = await farmvestApi.post(API_ENDPOINTS.loginWithOtp(), payload, {
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' }
        });

        const isMleEnabled = response.data?.mle_enabled === true || response.data?.mle_enabled === 'true';
        localStorage.setItem('mle_enabled', String(isMleEnabled));
        if (isMleEnabled && response.data?.server_public_key) {
            await mleService.setServerPublicKey(response.data.server_public_key);
        }
        return response.data;
    },
    getOrderTransferHistory: async (orderId: string): Promise<Array<{ transfer_id: number; animalkart_order_id: string; units_transferred: number; transfer_date: string | null; receiver_name: string; receiver_mobile: string; sender_name: string; sender_mobile: string }>> => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.getOrderTransferHistory(orderId));
            return response.data?.data ?? [];
        } catch {
            return [];
        }
    },

    sendTransferOtp: async (investorMobile: string, receiverName: string, receiverMobile: string, unitsToTransfer: number): Promise<string> => {
        const response = await farmvestApi.post('/api/transfer/send-transfer-otp', {
            investor_mobile: investorMobile,
            receiver_name: receiverName,
            receiver_mobile: receiverMobile,
            units_to_transfer: unitsToTransfer
        });
        return response.data.otp as string;
    },
    onboardAnimal: async (onboardingData: any) => {
        try {

            let url = API_ENDPOINTS.onboardAnimal();
            const fId = onboardingData.farm_id;
            if (fId !== undefined && fId !== null && !isNaN(Number(fId))) {
                url += `?farm_id=${fId}`;
            }

            const adminMobile = await getAdminMobile();

            const response = await farmvestApi.post(url, onboardingData, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Admin-Mobile': adminMobile
                }
            });
            return response.data;
        } catch (error: any) {
            if (error.response) {
            }
            throw error;
        }
    },
    allocateAnimal: async (shedId: string, allocations: { neckband_id: any; row_number: string; parking_id: string }[]) => {
        try {
            const url = API_ENDPOINTS.allocateAnimal(shedId);

            // The API expects a single allocation object at the root of the body:
            // { neckband_id, row_number, parking_id }
            // If the UI sends multiple, we loop through them to ensure all are processed.
            const responses = [];
            for (const allocation of allocations) {
                const response = await farmvestApi.post(url, allocation);
                responses.push(response.data);
            }

            // Return the last response or a summary if needed
            return responses.length > 0 ? responses[responses.length - 1] : { status: "success", count: 0 };
        } catch (error: any) {
            if (error.response) {
                console.error("Allocation Error:", error.response.data);
            }
            throw error;
        }
    },
    searchAnimal: async (queryStr: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.searchAnimal()}?query_str=${queryStr}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    // ============ ANIMALS ============
    // Added getTotalAnimals for Tag Number generation
    getTotalAnimalCount: async () => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.getTotalAnimals());
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getAnimalsByInvestor: async (investorId: number | string, params?: { page?: number; size?: number; farm_id?: string | number; shed_id?: string | number; search?: string; health_status?: string; type?: string }) => {
        try {
            let url = `${API_ENDPOINTS.getInvestorAnimals()}?investor_id=${investorId}`;
            if (params?.page) url += `&page=${params.page}`;
            if (params?.size) url += `&size=${params.size}`;
            if (params?.farm_id) url += `&farm_id=${params.farm_id}`;
            if (params?.shed_id) url += `&shed_id=${params.shed_id}`;
            if (params?.search) url += `&search=${encodeURIComponent(params.search)}`;
            if (params?.health_status) url += `&health_status=${encodeURIComponent(params.health_status)}`;
            if (params?.type && params.type !== 'ALL') url += `&type=${encodeURIComponent(params.type)}`;

            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // getAnimalsByInvestor was duplicated here by mistake. Removing this block.
    // identifier can be a neckband id, RFID, ear tag, or tag number
    getCalves: async (identifier: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getCalves()}?identifier=${encodeURIComponent(identifier)}`);
            return response.data;
        } catch (error: any) {
            return [];
        }
    },

    getAnimalHistory: async (animalIdOrTag: string) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getAnimalHistory()}?animal_id=${encodeURIComponent(animalIdOrTag)}`);
            return response.data;
        } catch (error: any) {
            // Also try fallback/different query param if needed
            try {
                const response = await farmvestApi.get(`${API_ENDPOINTS.getAnimalHistory()}?tag_no=${encodeURIComponent(animalIdOrTag)}`);
                return response.data;
            } catch (err: any) {
                throw err;
            }
        }
    },


    // AI Entry Integration
    submitAIEntry: async (payload: { ai_generate_date: string; device_id: string; is_ai_generated: boolean; semen_straw_type: string; semen_id?: string; bull_id?: string; bull_no?: string }) => {
        const response = await farmvestApi.post(API_ENDPOINTS.aiEntry(), payload);
        return response.data;
    },

    // ============ TICKETS ============

    createTicket: async (ticketType: string, payload: any) => {
        const response = await farmvestApi.post(`${API_ENDPOINTS.createTicket()}?ticket_type=${ticketType}`, payload);
        return response.data;
    },

    getTickets: async (params?: {
        ticket_type?: string;
        status_filter?: string;
        transfer_direction?: string;
        farm_id?: number;
        shed_id?: number;
        page?: number;
        size?: number;
        search?: string;
        start_date?: string;
        end_date?: string;
    }) => {
        const query = new URLSearchParams();
        if (params?.ticket_type) query.append('ticket_type', params.ticket_type);
        if (params?.status_filter) query.append('status_filter', params.status_filter);
        if (params?.transfer_direction) {
            let direction = params.transfer_direction;
            if (direction === 'TRANSFER_IN') direction = 'IN';
            if (direction === 'TRANSFER_OUT') direction = 'OUT';
            query.append('transfer_direction', direction);
        }
        if (params?.farm_id !== undefined && params?.farm_id !== null) {
            query.append('farm_id', params.farm_id.toString());
        }
        if (params?.shed_id !== undefined && params?.shed_id !== null) {
            query.append('shed_id', params.shed_id.toString());
        }
        if (params?.search) query.append('search', params.search);
        if (params?.start_date) query.append('start_date', params.start_date);
        if (params?.end_date) query.append('end_date', params.end_date);
        query.append('page', (params?.page || 1).toString());
        query.append('size', (params?.size || 15).toString());
        const response = await farmvestApi.get(`${API_ENDPOINTS.getHealthTickets()}?${query.toString()}`);
        return response.data;
    },

    assignTicket: async (id: number, payload: { assistant_id?: number; doctor_id?: number }) => {
        const response = await farmvestApi.post(API_ENDPOINTS.assignTickets(), {
            ticket_id: id,
            assistant_id: payload.assistant_id,
            doctor_id: payload.doctor_id
        });
        return response.data;
    },

    getDoctorAssistants: async () => {
        const response = await farmvestApi.get(API_ENDPOINTS.getMyAssistants());
        return response.data;
    },

    updateTreatmentDetails: async (payload: { id: number; disease?: string[]; description?: string; prescription?: string }) => {
        const response = await farmvestApi.post(API_ENDPOINTS.updateTreatmentDetails(), {
            ticket_id: payload.id,
            disease: payload.disease,
            description: payload.description,
            prescription: payload.prescription
        });
        return response.data;
    },

    // ============ MILK ============

    createMilkEntry: async (payload: any) => {
        const response = await farmvestApi.post(API_ENDPOINTS.createMilkEntry(), payload);
        return response.data;
    },

    getMilkEntries: async (params?: { page?: number; size?: number; farm_id?: number | string; shed_id?: number | string; timing?: string; milk_id?: string; location_id?: number | string; start_date?: string; end_date?: string }) => {
        const query = new URLSearchParams();
        query.append('page', (params?.page || 1).toString());
        query.append('size', (params?.size || 15).toString());
        if (params?.farm_id) query.append('farm_id', params.farm_id.toString());
        if (params?.shed_id) query.append('shed_id', params.shed_id.toString());
        if (params?.timing) query.append('timing', params.timing);
        if (params?.milk_id) query.append('milk_id', params.milk_id);
        if (params?.location_id) query.append('location_id', params.location_id.toString());
        if (params?.start_date) query.append('start_date', params.start_date);
        if (params?.end_date) query.append('end_date', params.end_date);
        const response = await farmvestApi.get(`${API_ENDPOINTS.getAllMilkEntries()}?${query.toString()}`);
        return response.data;
    },

    getMilkReport: async (params: { report_date: string; timing?: string; page?: number; size?: number; farm_id?: number | string; shed_id?: number | string; milk_id?: string; location_id?: number | string }) => {
        const query = new URLSearchParams();
        query.append('report_date', params.report_date);
        if (params.timing) query.append('timing', params.timing);
        query.append('page', (params.page || 1).toString());
        query.append('size', (params.size || 15).toString());
        if (params.farm_id) query.append('farm_id', params.farm_id.toString());
        if (params.shed_id) query.append('shed_id', params.shed_id.toString());
        if (params.milk_id) query.append('milk_id', params.milk_id);
        if (params.location_id) query.append('location_id', params.location_id.toString());
        const response = await farmvestApi.get(`${API_ENDPOINTS.getMilkReport()}?${query.toString()}`);
        return response.data;
    },

    // ============ LEAVE REQUESTS ============

    createLeaveRequest: async (payload: any) => {
        const response = await farmvestApi.post(API_ENDPOINTS.createLeaveRequest(), payload);
        return response.data;
    },

    getLeaveRequests: async (params?: { status?: string; page?: number; size?: number; search?: string; date?: string }, signal?: AbortSignal) => {
        const query = new URLSearchParams();
        if (params?.status) query.append('status', params.status);
        if (params?.search) query.append('search', params.search);
        if (params?.date) query.append('date', params.date);
        query.append('page', (params?.page || 1).toString());
        query.append('size', (params?.size || 15).toString());
        const response = await farmvestApi.get(`${API_ENDPOINTS.getLeaveRequests()}?${query.toString()}`, { signal });
        return response.data;
    },

    updateLeaveStatus: async (leaveId: number, payload: { status: string; rejection_reason?: string; leaves_id: number }) => {
        const response = await farmvestApi.put(API_ENDPOINTS.updateLeaveStatus(leaveId), payload);
        return response.data;
    },

    cancelLeaveRequest: async (leaveId: number) => {
        const response = await farmvestApi.put(API_ENDPOINTS.cancelLeaveRequest(leaveId));
        return response.data;
    },

    // ============ UNIT TRANSFER REQUESTS ============

    getPendingTransferTickets: async (params?: {
        page?: number;
        page_size?: number;
        search?: string;
        ticket_status?: string;
    }) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getPendingTransferTickets(), { params });
        const rawData = response.data?.data ?? [];
        const mappedData = rawData.map((item: any) => ({
            ...item,
            ticket_id: item.ticket_id || item.transfer_id || item.id || item.transfer_ticket_id
        }));

        return {
            data: mappedData,
            total: response.data?.total ?? 0,
            counts: response.data?.counts ?? { pending: 0, completed: 0, rejected: 0 },
            page: response.data?.page ?? 1,
            page_size: response.data?.page_size ?? 20,
            total_pages: response.data?.total_pages ?? 1,
        };
    },

    approveUnitTransfer: async (ticketId: number) => {
        const response = await farmvestApi.post(API_ENDPOINTS.approveUnitTransfer(ticketId), {});
        return response.data;
    },

    rejectUnitTransfer: async (ticketId: number, reason?: string) => {
        const response = await farmvestApi.post(API_ENDPOINTS.rejectUnitTransfer(ticketId), { reason: reason || '' });
        return response.data;
    },

    // ============ FARM DETAILS ============

    getFarmDetails: async (farmId: number) => {
        try {
            const response = await farmvestApi.get(`${API_ENDPOINTS.getFarmDetails()}?farm_id=${farmId}`);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
    getFarmStaff: async (farmId: number | string, shedId?: number | string) => {
        try {
            let url = `${API_ENDPOINTS.getFarmStaff()}?farm_id=${farmId}&is_active=true`;
            if (shedId) {
                url += `&shed_id=${shedId}`;
            }
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    // ============ EMPLOYEE UPDATE ============

    updateEmployee: async (payload: { user_id: number; first_name?: string; last_name?: string; email?: string; is_active?: boolean; role?: string; farm_id?: number; shed_id?: number; profile_image_url?: string; }) => {
        // Send common variations of keys to ensure backend picks it up
        const finalPayload = {
            ...payload,
            employee_id: payload.user_id, // some backends prefer employee_id 
            roles: payload.role ? [payload.role] : undefined,
            active_status: payload.is_active ? 1 : 0
        };
        // Backend expects user_id as a query parameter for the PUT request
        const url = `${API_ENDPOINTS.updateEmployee()}?user_id=${payload.user_id}`;
        const response = await farmvestApi.put(url, finalPayload);
        return response.data;
    },

    updateUserDetails: async (payload: { name?: string; email?: string; address?: string; profile?: string }) => {
        const response = await farmvestApi.put(API_ENDPOINTS.updateFarmVestUserDetails(), payload);
        return response.data;
    },


    // Global withdrawal availability — single app-wide toggle controlling whether the
    // withdrawal request flow is enabled for all investors.
    // PATCH /api/app-config/withdrawal?enabled=true|false
    updateWithdrawalConfig: async (enabled: boolean) => {
        const response = await farmvestApi.patch(`/api/app-config/withdrawal?enabled=${enabled}`);
        return response.data;
    },

    // Current global withdrawal availability — GET /api/app-config/withdrawal.
    // Normalises the various possible response shapes down to a single boolean.
    getWithdrawalConfig: async (): Promise<boolean> => {
        const response = await farmvestApi.get('/api/app-config/withdrawal');
        const data = response.data ?? {};
        const raw = data.withdrawal_enabled ?? data.enabled ?? data.data?.withdrawal_enabled ?? data.data?.enabled ?? data.value ?? data.is_enabled;
        return raw === true || raw === 'true';
    },

    getNotificationHistory: async (params?: { page?: number; size?: number; farm_id?: number | string; shed_id?: number | string; alert_type?: string }) => {
        try {
            const payload = {
                page: params?.page || 1,
                size: params?.size || 20,
                farm_id: params?.farm_id || undefined,
                shed_id: params?.shed_id || undefined,
                alert_type: params?.alert_type || undefined
            };

            const response = await farmvestApi.post(API_ENDPOINTS.getNotificationHistory(), payload);
            return (response.data);
        } catch (error: any) {
            throw error;
        }
    },

    // ============ ACCOUNT ACTIVATION/DEACTIVATION (OTP FLOW) ============

    requestReactivationOtp: async (_mobile: string, _channel: string = 'whatsapp') => {
        // Mock success as there is no backend endpoint for this in Farmvest.
        // This follows the same pattern as the Login flow in this dashboard.
        return { message: 'OTP sent successfully' };
    },

    confirmReactivation: async (mobile: string, _otp: string, remarks?: string) => {
        // Since OTP sending is mocked, we proceed directly to the activation call.
        return farmvestService.activateUser(mobile, remarks);
    },

    requestDeactivationOtp: async (_mobile: string, _channel: string = 'whatsapp') => {
        // Mock success
        return { message: 'OTP sent successfully' };
    },

    confirmDeactivation: async (mobile: string, _otp: string, remarks?: string) => {
        // Since OTP sending is mocked, we proceed directly to the deactivation call.
        return farmvestService.deactivateUser(mobile, remarks);
    },

    // ============ IOT ALERTS ============
    getNeckbandAlerts: async (params: { page?: number; size?: number; farm_id?: number | string; shed_id?: number | string; alert_type?: string }) => {
        try {
            const url = corsProxiedUrl(API_ENDPOINTS.getNotificationHistory());
            const response = await axios.post(url, params, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getAlertRules: async (farmId?: number | string, shedId?: number | string) => {
        try {
            let url = API_ENDPOINTS.getAlertRules();
            const query = new URLSearchParams();
            if (farmId) query.append('farm_id', farmId.toString());
            if (shedId) query.append('shed_id', shedId.toString());

            if (query.toString()) {
                url += (url.includes('?') ? '&' : '?') + query.toString();
            }

            const response = await farmvestApi.get(url);
            const rawData = response.data?.data || response.data?.rules || response.data?.alert_configs || (Array.isArray(response.data) ? response.data : null);

            if (rawData && Array.isArray(rawData)) {
                return {
                    status: "success",
                    rules: rawData.map((rule: any) => ({
                        ...rule,
                        roles: rule.target_roles || rule.roles || []
                    }))
                };
            }
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    getIoTAlertUsers: async (deviceId: string) => {
        try {
            const response = await farmvestApi.post(`${API_CONFIG.getFarmVestBaseUrl()}/api/notifications/getIOTAlertUsers`, {
                device_id: deviceId
            });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    // ============ ANIMAL RELATED USERS (for Managing Employees tab) ============

    getAnimalRelatedUsers: async (neckbandId: string) => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.getAnimalRelatedUsers(neckbandId));
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    addAnimalRelatedUser: async (neckbandId: string, role: string) => {
        try {
            const response = await farmvestApi.post(API_ENDPOINTS.addAnimalRelatedUser(neckbandId), { role });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    removeAnimalRelatedUser: async (neckbandId: string, role: string) => {
        try {
            // Note: If DELETE doesn't take a body, we might need query params or a specific sub-path
            const response = await farmvestApi.delete(API_ENDPOINTS.removeAnimalRelatedUser(neckbandId), { data: { role } });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    updateAlertRule: async (payload: { alert_type: string; roles: string[]; farm_id?: number | string; shed_id?: number | string }) => {
        const backendPayload: any = {
            alert_type: payload.alert_type,
            target_roles: payload.roles
        };
        if (payload.farm_id) backendPayload.farm_id = payload.farm_id;
        if (payload.shed_id) backendPayload.shed_id = payload.shed_id;

        try {
            const response = await farmvestApi.put(API_ENDPOINTS.updateAlertRule(), backendPayload);
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                // Backend requires POST to create if rule doesn't exist yet
                const postResponse = await farmvestApi.post(API_ENDPOINTS.updateAlertRule(), backendPayload);
                return postResponse.data;
            }
            throw error;
        }
    },

    removeAlertRuleRole: async (alertType: string, role: string) => {
        try {
            const response = await farmvestApi.delete(API_ENDPOINTS.removeAlertRuleRole(alertType, role));
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },

    resetAlertRule: async (alertType: string) => {
        const response = await farmvestApi.delete(API_ENDPOINTS.resetAlertRule(alertType));
        return response.data;
    },

    getIntransitAnimals: async () => {
        try {
            // Get admin mobile from session for headers
            const sessionStr = localStorage.getItem('ak_dashboard_session');
            let adminMobile = '';
            if (sessionStr) {
                try {
                    const session = JSON.parse(sessionStr);
                    adminMobile = session.mobile;
                } catch (e) { }
            }

            // Fetching from the specific procurement roster API
            const response = await animalKartApi.get(API_ENDPOINTS.getProcurementRoster(),
                {
                    headers: {
                        'X-Admin-Mobile': adminMobile,
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching procurement roster:', error);
            throw error;
        }
    },

    // ============ ROSTER ============
    getRoster: async (stage: string, page: number = 1, pageSize: number = 20, date?: string, farmId?: string | number, cpfFilter?: string, deliveredOverdue?: boolean | string, mobile?: string) => {
        try {
            const sessionStr = localStorage.getItem('ak_dashboard_session');
            let adminMobile = '';
            if (sessionStr) {
                try {
                    const session = JSON.parse(sessionStr);
                    adminMobile = session.mobile;
                } catch (e) { }
            }

            const query = new URLSearchParams();
            query.append('stage', stage);
            query.append('page', page.toString());
            query.append('page_size', pageSize.toString());
            if (date) {
                query.append('date', date);
            }
            if (farmId) {
                query.append('filterFarmId', farmId.toString());
            }
            if (cpfFilter !== undefined && cpfFilter !== null && cpfFilter !== '') {
                query.append('cpf_filter', cpfFilter);
            }
            if (deliveredOverdue !== undefined && deliveredOverdue !== null && deliveredOverdue !== '') {
                query.append('delivered_overdue', deliveredOverdue.toString());
            }
            if (mobile) {
                query.append('mobile', mobile);
            }

            const url = `${API_ENDPOINTS.getRoster()}?${query.toString()}`;
            const response = await animalKartApi.get(url, {
                headers: {
                    'X-Admin-Mobile': adminMobile,
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching roster:', error);
            throw error;
        }
    },
    getRevenueTrend: async (months: number = 12, farm_id?: number, shed_id?: number, granularity: string = 'monthly') => {
        try {
            let url = API_ENDPOINTS.getRevenueTrend(months);
            const params = new URLSearchParams();
            if (farm_id) params.append('farm_id', farm_id.toString());
            if (shed_id) params.append('shed_id', shed_id.toString());
            params.append('granularity', granularity);
            const queryString = params.toString();
            const response = await farmvestApi.get(queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url);
            return response.data;
        } catch (error) {
            console.error('Error fetching revenue trend:', error);
            throw error;
        }
    },
    getSlotCameras: async (shedId: number, row: string, slot: string): Promise<string[]> => {
        const response = await farmvestApi.get(`/api/shed/${shedId}/slot_cameras`, { params: { row, slot } });
        return response.data.cameras ?? [];
    },
    getPaymentStats: async (): Promise<{
        total_count: number; total_amount: number;
        verified_count: number; verified_amount: number;
        pending_count: number; pending_amount: number;
        rejected_count: number; rejected_amount: number;
        pending_verification_count?: number; pending_verification_amount?: number;
        pending_verification_cpf_units?: number;
        verified_cpf_units?: number;
        pending_cpf_units?: number;
        rejected_cpf_units?: number;
        total_units?: number;
        total_payments?: number;
        verified_payments?: number;
        verified_units?: number;
        pending_verification_payments?: number;
        pending_verification_units?: number;
        rejected_payments?: number;
        rejected_units?: number;
        defaulter_investors_count?: number;
        defaulter_unpaid_units?: number;
        total_investment_units?: number;
        total_investor_payouts?: number;
        total_investor_payouts_count?: number;
    }> => {
        const response = await farmvestApi.get('/api/payments/stats');
        return response.data;
    },

    getPendingPayments: async (params?: {
        page?: number;
        size?: number;
        search?: string;
        payment_status?: string;
        payment_mode?: string;
        bank_name?: string;
        start_date?: string;
        end_date?: string;
        transaction_date?: string;
    }): Promise<{ data: any[]; total: number; page: number; size: number; total_pages: number; total_amount: number; status_counts: { PENDING: number; VERIFIED: number; REJECTED: number; PENDING_VERIFICATION: number; total_count: number } }> => {
        // Strip undefined/null and map size to page_size
        const mappedParams = {
            ...params,
            page_size: params?.size,
        };
        const cleanParams = Object.fromEntries(
            Object.entries(mappedParams).filter(([k, v]) => k !== 'size' && v !== undefined && v !== null && v !== '')
        );
        const response = await farmvestApi.get('/api/payments/pending', { params: cleanParams });
        const statusCounts = response.data?.status_counts ?? {};
        return {
            data: response.data?.data ?? [],
            total: response.data?.status_counts?.total_count ?? response.data?.total ?? 0,
            page: response.data?.page ?? 1,
            size: response.data?.size ?? response.data?.page_size ?? 15,
            total_pages: response.data?.total_pages ?? 1,
            total_amount: response.data?.total_amount ?? 0,
            status_counts: {
                PENDING: statusCounts.PENDING ?? 0,
                VERIFIED: statusCounts.VERIFIED ?? 0,
                REJECTED: statusCounts.REJECTED ?? 0,
                PENDING_VERIFICATION: statusCounts.PENDING_VERIFICATION ?? 0,
                total_count: statusCounts.total_count ?? response.data?.total ?? 0,
            },
        };
    },

    approvePayment: async (paymentId: number): Promise<any> => {
        const response = await farmvestApi.put(`/api/payments/${paymentId}/status`, { action: 'VERIFIED' });
        return response.data;
    },

    updateCpfStatus: async (params: {
        orderId: string;
        userId: string;
        cpfUnitCost: number;
    }): Promise<void> => {
        await animalKartApi.post('/purchases/units/update-cpf', {
            orderId: params.orderId,
            userId: params.userId,
            withCpf: true,
            cpfUnitCost: params.cpfUnitCost,
        });
    },

    redeemCoinsInAnimalKart: async (params: {
        mobile: string;
        buffalo_id?: string | null;
        orderid: string;
        coins: number;
        cpf_start_date: string;
    }): Promise<void> => {
        const body: Record<string, any> = {
            mobile: params.mobile,
            orderid: params.orderid,
            coins: params.coins,
            type: 'CPF_REDEMPTION',
            cpf_start_date: params.cpf_start_date,
        };
        // Only include buffalo_id when it's a real UUID (post-onboarding)
        if (params.buffalo_id) {
            body['buffalo_id'] = params.buffalo_id;
        }
        await animalKartApi.post('/users/coinTransaction/redeem', body);
    },

    rejectPayment: async (paymentId: number, remarks: string): Promise<void> => {
        await farmvestApi.put(`/api/payments/${paymentId}/status`, { action: 'REJECTED', remarks });
    },

    editPayment: async (paymentId: number, body: {
        payment_mode?: string; utr_number?: string; bank_name?: string;
        transaction_date?: string; transfer_mode?: string; ifsc_code?: string;
        paid_to?: string; paid_mobile?: string;
        payment_screenshot_url?: string; cheque_front_url?: string; cheque_back_url?: string;
        manual_payment_receipt_url?: string; remarks?: string;
    }): Promise<void> => {
        await farmvestApi.patch(`/api/payments/${paymentId}/edit`, body);
    },

    submitCpfPayment: async (mode: string, body: Record<string, unknown>): Promise<void> => {
        await farmvestApi.post(`/api/payments/?payment_mode=${mode}`, body);
    },

    getPaymentLogs: async (paymentId: number): Promise<Array<{
        log_id: number;
        action: string;
        performed_by_name: string | null;
        notes: string | null;
        timestamp: string | null;
    }>> => {
        const response = await farmvestApi.get(`/api/payments/${paymentId}/logs`);
        return response.data?.data ?? [];
    },

    getWithdrawalLogs: async (withdrawalId: number): Promise<Array<{
        log_id: number;
        action: string;
        performed_by_name: string | null;
        notes: string | null;
        timestamp: string | null;
    }>> => {
        const response = await farmvestApi.get(`/api/wallet/withdraw/${withdrawalId}/logs`);
        return response.data?.data ?? [];
    },

    getOnboardingLogs: async (mobile: string): Promise<Array<{
        log_id: number;
        event: string;
        performed_by_name: string | null;
        reason: string | null;
        created_at: string | null;
        extra_data?: any;
    }>> => {
        const response = await farmvestApi.get(`/api/onboarding/logs?mobile=${mobile}`);
        return response.data?.data ?? [];
    },

    getInvestorPendingCpfOrders: async (mobile: string): Promise<Array<{
        id: string;
        numUnits: number;
        totalCost: number;
        status: string;
        paymentStatus: string;
        placedAt: string;
        withCpf?: boolean;
    }>> => {
        try {
            const url = `/purchases/units/${mobile}?withCpf=false`;
            const response = await animalKartApi.get(url, { headers: { accept: 'application/json' } });
            const orders: any[] = response.data?.orders ?? [];
            return orders.map((o: any) => ({
                id: (o.id ?? o.orderId ?? o.animalkart_order_id ?? '').toString(),
                numUnits: o.numUnits ?? o.number_of_units ?? 0,
                totalCost: o.totalCost ?? o.total_cost ?? 0,
                status: o.status ?? o.stage ?? '',
                paymentStatus: (o.paymentStatus ?? o.payment_status ?? '').toString(),
                placedAt: o.placedAt ?? o.orderDate ?? '',
                withCpf: o.withCpf,
            })).filter(o => {
                if (!o.id) return false;
                return o.withCpf === false;
            });
        } catch {
            return [];
        }
    },

    // Looks up a single Animalkart order's own paymentStatus (PAID / PENDING_VERIFICATION),
    // independent of the withCpf filter used by getInvestorPendingCpfOrders — the order
    // under farmvest verification may already have CPF attached.
    getAnimalkartOrderPaymentStatus: async (mobile: string, orderId: string): Promise<string | null> => {
        try {
            const url = `/purchases/units/${mobile}?withCpf=false`;
            const response = await animalKartApi.get(url, { headers: { accept: 'application/json' } });
            const orders: any[] = response.data?.orders ?? [];
            const stripped = orderId.replace(/^OD#?/, '');
            const candidates = new Set([orderId, stripped, `OD${stripped}`, `OD#${stripped}`]);
            const match = orders.find((o: any) => {
                const id = (o.id ?? o.orderId ?? o.animalkart_order_id ?? '').toString();
                return candidates.has(id);
            });
            return match ? ((match.paymentStatus ?? match.payment_status ?? '').toString() || null) : null;
        } catch {
            return null;
        }
    },

    getInvestorCoinTransactions: async (mobile: string): Promise<{
        stats: { remaining_coins: number; total_coins: number; spending_coins: number; direct_referrals_coins: number; indirect_referrals_coins: number };
        transactions: any[];
    }> => {
        const response = await animalKartApi.get(`/users/coinTransaction/${mobile}`);
        return response.data;
    },

    getOrderDetails: async (orderId: string) => {
        try {
            const response = await animalKartApi.get(API_ENDPOINTS.getInvestorDetailsByOrder(orderId));
            return response.data;
        } catch (error) {
            console.error('[API] getOrderDetails error:', error);
            throw error;
        }
    },
    getAllVisits: async (params?: { page?: number; size?: number; location_name?: string; farm_id?: number; search?: string; status?: string; visit_date?: string; visit_id?: number | string; date_range?: string }) => {
        try {
            const { page = 1, size = 15, location_name, farm_id, search, status, visit_date, visit_id, date_range } = params || {};

            // Get admin mobile from session for header
            const sessionStr = localStorage.getItem('ak_dashboard_session');
            let adminMobile = '';
            if (sessionStr) {
                try {
                    const session = JSON.parse(sessionStr);
                    adminMobile = session.mobile;
                } catch (e) { }
            }

            let url = `${API_ENDPOINTS.getAllVisits()}?page=${page}&size=${size}`;
            if (location_name && location_name !== 'All' && location_name !== 'All Locations') {
                url += `&location_name=${encodeURIComponent(location_name)}`;
            }
            if (farm_id && farm_id !== 0) {
                url += `&farm_id=${farm_id}`;
            }
            if (search) {
                url += `&search=${encodeURIComponent(search)}`;
            }
            if (visit_id) {
                url += `&visit_id=${encodeURIComponent(visit_id)}`;
            }
            if (status && status !== 'All') {
                url += `&visit_status=${encodeURIComponent(status)}`;
            }
            if (visit_date) {
                url += `&visit_date=${encodeURIComponent(visit_date)}`;
            }
            if (date_range) {
                url += `&date_range=${encodeURIComponent(date_range)}`;
            }

            const response = await farmvestApi.get(url, {
                headers: {
                    'X-Admin-Mobile': adminMobile
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getNotificationsHistory: async (page: number = 1, size: number = 20) => {
        try {
            const url = API_ENDPOINTS.getNotificationsHistory(page, size);
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getNotificationsUnreadCount: async () => {
        try {
            const response = await farmvestApi.get(API_ENDPOINTS.getNotificationsUnreadCount());
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    markNotificationsRead: async (payload: { ids?: number[]; reference_ids?: number[]; mark_all?: boolean }) => {
        try {
            const response = await farmvestApi.patch(API_ENDPOINTS.markNotificationsRead(), payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    searchUserByMobilePrefix: async (mobile: string) => {
        try {
            // Combined search for employees and investors
            const [employeeRes, investorRes] = await Promise.all([
                farmvestApi.get(`${API_ENDPOINTS.searchEmployee()}?mobile=${mobile}`).catch(() => ({ data: { employees: [] } })),
                farmvestApi.get(`${API_ENDPOINTS.getAllInvestors()}?search=${mobile}`).catch(() => ({ data: [] }))
            ]);

            const employees = (employeeRes.data?.employees || []).map((e: any) => ({
                ...e,
                type: 'employee',
                displayName: e.full_name || e.name,
                mobile: e.mobile || e.mobile_number
            }));

            const investors = (investorRes.data || []).map((i: any) => ({
                ...i,
                type: 'investor',
                displayName: i.name || i.full_name,
                mobile: i.mobile || i.investor_mobile
            }));

            return { users: [...employees, ...investors] };
        } catch (error) {
            console.error("Unified search failed:", error);
            return { users: [] };
        }
    },

    // ─── Investor Onboarding ─────────────────────────────────────────────────
    copyOnboardingLink: async (mobile: string, name?: string | null) => {
        const response = await farmvestApi.post('/api/onboarding/copy-link', { mobile, name });
        return response.data as { invitation_id: string; token: string; mobile: string; onboarding_url?: string };
    },

    sendOnboardingInvite: async (mobile: string, name?: string | null) => {
        const response = await farmvestApi.post('/api/onboarding/send-invite', { mobile, name });
        return response.data as { invitation_id: string; token: string; mobile: string; expires_at: string; whatsapp_sent: boolean; whatsapp_message: string; onboarding_url?: string };
    },

    listOnboardingInvitations: async (params?: { page?: number; size?: number; search?: string; status_filter?: string; sent_date?: string }) => {
        const response = await farmvestApi.get('/api/onboarding/list', { params });
        return response.data as {
            data: any[];
            counts: { total: number; pending: number; used: number; submissions: number; expired: number; rejected: number; verified: number; };
            pagination: { current_page: number; total_pages: number; total_items: number; items_per_page: number };
        };
    },

    verifyOnboardingSubmission: async (invitationId: string) => {
        const response = await farmvestApi.post(`/api/onboarding/${invitationId}/verify`);
        return response.data;
    },

    updateOnboardingSubmission: async (invitationId: string, data: any) => {
        const response = await farmvestApi.patch(`/api/onboarding/${invitationId}`, data);
        return response.data;
    },

    rejectOnboardingSubmission: async (invitationId: string, rejectionReason?: string, remarks?: string) => {
        const response = await farmvestApi.post(`/api/onboarding/${invitationId}/reject`, { rejection_reason: rejectionReason, remarks });
        return response.data;
    },

    exportOnboardingSubmissions: async () => {
        const response = await farmvestApi.get('/api/onboarding/export', { responseType: 'blob' });
        return response.data;
    },

    // Public — no auth header needed, must hit backend directly (not dashboard URL)
    validateOnboardingToken: async (token: string) => {
        const base = FARMVEST_API_CONFIG.getBaseUrl();
        const response = await axios.get(`${base}/api/onboarding/public/validate-token?token=${encodeURIComponent(token)}`);
        return response.data as {
            mobile: string; token: string; valid: boolean;
            first_name?: string; last_name?: string; email?: string;
            address?: string; city?: string; state?: string; pincode?: string;
        };
    },

    submitOnboardingForm: async (payload: {
        token: string;
        first_name: string;
        last_name: string;
        email?: string;
        address?: string;
        city?: string;
        state?: string;
        pincode?: string;
        aadhar_number: string;
        aadhar_front_photo: string;
        aadhar_back_photo: string;
        pancard_number: string;
        pancard_photo: string;
        bank_passbook_photo?: string;
        account_holder_name: string;
        account_number: string;
        ifsc_code: string;
        nominee_name: string;
        nominee_mobile: string;
        nominee_relation: string;
        nominee_aadhar: string;
        nominee_pancard_number?: string;
        nominee_pancard_photo?: string;
    }) => {
        const base = FARMVEST_API_CONFIG.getBaseUrl();
        const response = await axios.post(`${base}/api/onboarding/public/submit`, payload);
        return response.data;
    },

    // Public draft fetch for the unauthenticated onboarding form. Uses raw axios
    // (NOT farmvestApi) so a 401 does not trip the global interceptor that hard-
    // redirects to /login — a public visitor has no session/token, so the authed
    // instance would always bounce them off the form.
    getOnboardingDraftPublic: async (investor_mobile: string) => {
        try {
            const base = FARMVEST_API_CONFIG.getBaseUrl();
            const headers: Record<string, string> = {};
            let activeToken: string | null = null;
            try {
                const savedSession = localStorage.getItem('ak_dashboard_session');
                if (savedSession) {
                    const session = JSON.parse(savedSession);
                    if (session.access_token && session.access_token !== 'static_staging_token') {
                        activeToken = session.access_token;
                    }
                }
            } catch (e) { }

            if (activeToken) {
                headers['Authorization'] = `Bearer ${activeToken}`;
            }
            const response = await axios.get(
                `${base}/api/animal/draft?animalkart_order_id=&investor_mobile=${encodeURIComponent(investor_mobile)}`,
                { headers }
            );
            return response.data;
        } catch (error: any) {
            // 404 means no draft exists — return null so callers can branch gracefully
            if (error?.response?.status === 404) return null;
            throw error;
        }
    },

    // Public draft save for the unauthenticated onboarding form. Raw axios for the
    // same reason as getOnboardingDraftPublic above.
    saveOnboardingDraftPublic: async (payload: {
        draft_data: Record<string, any>;
        current_step: number;
        investor_mobile: string;
        animalkart_order_id: string;
        animalkart_buffalo_id?: string;
    }) => {
        const base = FARMVEST_API_CONFIG.getBaseUrl();
        const headers: Record<string, string> = {};
        let activeToken: string | null = null;
        try {
            const savedSession = localStorage.getItem('ak_dashboard_session');
            if (savedSession) {
                const session = JSON.parse(savedSession);
                if (session.access_token && session.access_token !== 'static_staging_token') {
                    activeToken = session.access_token;
                }
            }
        } catch (e) { }

        if (activeToken) {
            headers['Authorization'] = `Bearer ${activeToken}`;
        }
        const response = await axios.post(`${base}/api/animal/draft/save`, payload, { headers });
        return response.data;
    },

    // ── Revenue ───────────────────────────────────────────────────────────────
    getRevenueSummary: async (month: number, year: number) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getRevenueSummary(month, year));
        return response.data;
    },
    verifyPayouts: async (revenueIds: number[]) => {
        const response = await farmvestApi.post(API_ENDPOINTS.verifyPayouts(), { revenue_ids: revenueIds });
        return response.data;
    },
    confirmPayment: async (revenueIds: number[], adminProofUrl?: string, utrNumber?: string, paymentAmount?: number, settlementAmount?: number) => {
        const response = await farmvestApi.post(API_ENDPOINTS.confirmPayment(), {
            revenue_ids: revenueIds,
            admin_proof_url: adminProofUrl || null,
            utr_number: utrNumber?.trim() || null,
            ...(paymentAmount != null ? { payment_amount: paymentAmount } : {}),
            ...(settlementAmount != null ? { settlement_amount: settlementAmount } : {}),
        });
        return response.data;
    },
    triggerRevenueGeneration: async (month: number, year: number) => {
        const response = await farmvestApi.post(API_ENDPOINTS.triggerRevenueGeneration(month, year));
        return response.data;
    },
    getInvestorMonthlyRevenue: async (investorId: number, month: number, year: number) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getInvestorMonthlyRevenue(investorId, month, year));
        return response.data;
    },
    getInvestorRevenueHistory: async (investorId: number) => {
        const response = await farmvestApi.get(`/api/revenue/investor/${investorId}/history`);
        return response.data;
    },
    bulkVerifyRevenue: async (month: number, year: number) => {
        const response = await farmvestApi.post(API_ENDPOINTS.bulkVerifyRevenue(month, year));
        return response.data;
    },
    bulkConfirmRevenue: async (month: number, year: number) => {
        const response = await farmvestApi.post(API_ENDPOINTS.bulkConfirmRevenue(month, year));
        return response.data;
    },
    exportRevenueCsv: async (month: number, year: number) => {
        const response = await farmvestApi.get(API_ENDPOINTS.exportRevenueCsv(month, year), { responseType: 'blob' });
        return response.data;
    },
    getRevenueGenerationPreview: async (month: number, year: number) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getRevenueGenerationPreview(month, year));
        return response.data;
    },

    // --- Added for Investor Details ---
    getInvestorFarms: async (investorId: number | string) => {
        const response = await farmvestApi.get(`${API_ENDPOINTS.getInvestorFarms()}?investor_id=${investorId}`);
        return response.data;
    },

    sendCpfReceipt: async (paymentId: number): Promise<boolean> => {
        const response = await farmvestApi.post('/api/payments/send-receipt', { payment_id: paymentId });
        return response.data?.status === 'success';
    },

    // ── Wallet ────────────────────────────────────────────────────────────────
    getWalletBalance: async (userId: number | string) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getWalletBalance(userId));
        return response.data as { status: string; user_id: number; balance: number };
    },
    getWalletTransactions: async (userId: number | string, limit = 50) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getWalletTransactions(userId, limit));
        return response.data as {
            status: string;
            user_id: number;
            transactions: {
                transaction_id: number;
                amount: number;
                type: 'CREDIT' | 'DEBIT';
                description: string;
                transaction_date: string;
                animal_id: number | null;
                revenue_id: number | null;
                cycle_start: string | null;
                cycle_end: string | null;
            }[];
        };
    },
    getAllWithdrawals: async (
        statusOrParams?: string | { page?: number; size?: number; status?: string; date?: string }
    ) => {
        let cleanParams: any = {};
        if (typeof statusOrParams === 'string') {
            cleanParams.status = statusOrParams;
        } else if (statusOrParams && typeof statusOrParams === 'object') {
            cleanParams = Object.fromEntries(
                Object.entries(statusOrParams).filter(([, v]) => v !== undefined && v !== null && v !== '')
            );
        }

        const response = await farmvestApi.get('/api/wallet/withdraw/all', { params: cleanParams });
        return response.data as {
            status: string;
            total: number;
            total_count?: number;
            status_counts?: Record<string, number>;
            status_amounts?: Record<string, number>;
            withdrawals: {
                withdrawal_id: number;
                user_id: number;
                amount: number;
                status: string;
                created_at: string;
                updated_at: string;
                razorpay_payout_id: string | null;
                admin_notes: string | null;
                investor_name: string | null;
                investor_mobile: string | null;
                investor_email: string | null;
                account_number: string | null;
                ifsc_code: string | null;
                account_holder_name: string | null;
                bank_name: string | null;
                wallet_balance: number | null;
            }[];
            page?: number;
            total_pages?: number;
        };
    },
    getWithdrawalHistory: async (userId: number | string) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getWithdrawalHistory(userId));
        return response.data as {
            status: string;
            withdrawals: {
                withdrawal_id: number;
                amount: number;
                status: string;
                created_at: string;
                updated_at: string;
                razorpay_payout_id: string | null;
                admin_notes: string | null;
            }[];
        };
    },
    creditWallet: async (payload: {
        user_id: number;
        amount: number;
        description: string;
        animal_id?: number;
        revenue_id?: number;
        cycle_start?: string;
        cycle_end?: string;
    }) => {
        const response = await farmvestApi.post(API_ENDPOINTS.creditWallet(), payload);
        return response.data;
    },
    updatePayoutStatus: async (payload: {
        withdrawal_id: number;
        razorpay_payout_id?: string;
        status: 'success' | 'failed' | 'rejected';
        failure_reason?: string;
        admin_proof_image_url?: string;
    }) => {
        const response = await farmvestApi.post(API_ENDPOINTS.updatePayoutStatus(), payload);
        return response.data;
    },

    exportDuePayouts: async (date?: string, toDate?: string, status?: string): Promise<Blob> => {
        const response = await farmvestApi.get(API_ENDPOINTS.exportDuePayouts(date, toDate, status), { responseType: 'blob' });
        return response.data;
    },

    // Full (unpaginated) export of withdrawals — GET /api/wallet/withdraw/all/export.
    // Returns JSON records; the caller formats them into a CSV file.
    exportWithdrawals: async (status?: string, date?: string) => {
        const params = Object.fromEntries(
            Object.entries({ status, date }).filter(([, v]) => v !== undefined && v !== null && v !== '')
        );
        const response = await farmvestApi.get('/api/wallet/withdraw/all/export', { params });
        return response.data as {
            status: string;
            total: number;
            total_amount: number;
            data: Array<{
                sno?: number;
                withdrawal_id: number;
                amount: number;
                status: string;
                created_at: string;
                updated_at?: string;
                razorpay_payout_id?: string | null;
                utr_number?: string | null;
                admin_notes?: string | null;
                investor_name?: string | null;
                investor_mobile?: string | null;
                investor_email?: string | null;
                account_number?: string | null;
                ifsc_code?: string | null;
                account_holder_name?: string | null;
                wallet_balance?: number | null;
            }>;
        };
    },

    bulkReconcileWallet: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await farmvestApi.post('/api/wallet/withdraw/bulk-reconcile', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    getDuePayouts: async (date?: string) => {
        const response = await farmvestApi.get(API_ENDPOINTS.getDuePayouts(date));
        return response.data as {
            status: string;
            date: string;
            total_investors: number;
            total_payout: number;
            data: Array<{
                investor_id: number;
                investor_name: string;
                investor_mobile: string | null;
                gross_amount: number;
                cgf_deducted: number;
                cpf_deducted: number;
                net_payout: number;
                animal_count: number;
                status: string;
                account_holder_name: string | null;
                account_number: string | null;
                ifsc_code: string | null;
                wallet_balance: number | null;
            }>;
        };
    },

    getAllPendingPayouts: async () => {
        const response = await farmvestApi.get(API_ENDPOINTS.getAllPendingPayouts());
        return response.data as {
            status: string;
            as_of: string;
            total_investors: number;
            total_pending_payout: number;
            data: Array<{
                investor_id: number;
                investor_name: string;
                investor_mobile: string | null;
                account_holder_name: string | null;
                account_number: string | null;
                ifsc_code: string | null;
                bank_image_url: string | null;
                wallet_balance: number | null;
                months_count: number;
                total_gross: number;
                total_cgf: number;
                total_cpf: number;
                total_net: number;
                months: Array<{
                    cycle_date: string;
                    gross: number;
                    cgf: number;
                    cpf: number;
                    net: number;
                    animal_count: number;
                    status: string;
                    revenue_ids: number[];
                }>;
            }>;
        };
    },

    getScreenAccess: async (userId: number) => {
        const response = await farmvestApi.get(`/api/employee/screen_access/${userId}`);
        return response.data as { status: string; data: { user_id: number; screen_access: Record<string, string> } };
    },

    updateScreenAccess: async (userId: number, screenAccess: Record<string, string>) => {
        const response = await farmvestApi.put(`/api/employee/screen_access/${userId}`, screenAccess);
        return response.data as { status: string; message: string; data: { user_id: number; screen_access: Record<string, string> } };
    },

    // ── Permissions / Roles ──────────────────────────────────────────────────
    getAllRoles: async () => {
        const response = await farmvestApi.get('/api/permissions/roles/all');
        return response.data as { status: string; roles: { name: string; description: string; screen_access: Record<string, string> }[] };
    },

    getRoleByName: async (roleName: string) => {
        const response = await farmvestApi.get(`/api/permissions/roles/${roleName}`);
        return response.data as { status: string; role: { name: string; description: string; screen_access: Record<string, string> } };
    },

    getRolePermissions: async (roleName: string) => {
        const response = await farmvestApi.get(`/api/permissions/${roleName}`);
        return response.data as {
            status: string;
            role_name: string;
            permissions: { id: number; module_name: string; can_read: boolean; can_write: boolean; can_edit: boolean; can_delete: boolean }[];
        };
    },

    updateRolePermissions: async (roleName: string, permissions: { module_name: string; can_read: boolean; can_write: boolean; can_edit: boolean; can_delete: boolean }[]) => {
        const response = await farmvestApi.put('/api/permissions/update', { role_name: roleName, permissions });
        return response.data as { status: string; message: string };
    },

    createRole: async (name: string, description: string, screenAccess?: Record<string, string>) => {
        const response = await farmvestApi.post('/api/permissions/roles', { name, description, screen_access: screenAccess ?? {} });
        return response.data as { status: string; message: string };
    },

    updateRoleScreenAccess: async (roleName: string, screenAccess: Record<string, string>) => {
        const response = await farmvestApi.put(`/api/permissions/roles/${roleName}/screen_access`, screenAccess);
        return response.data as { status: string; message: string };
    },

    regenerateCpfLink: async (params: { payment_id?: number; animalkart_order_id?: string }) => {
        const response = await farmvestApi.post('/api/payments/regenerate-cpf-link', params);
        return response.data as {
            status: string;
            message: string;
            data: {
                farmvest_payment_id: number;
                razorpay_link_id: string;
                payment_link_url: string;
                amount: number;
                expiry: string;
                animalkart_order_id: string;
            };
        };
    },

    getAssetValues: async (params?: { page?: number; size?: number; farm_id?: number | string; shed_id?: number | string }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params) {
                if (params.page !== undefined) queryParams.set('page', String(params.page));
                if (params.size !== undefined) queryParams.set('size', String(params.size));
                if (params.farm_id !== undefined && params.farm_id !== 'All' && params.farm_id !== '') queryParams.set('farm_id', String(params.farm_id));
                if (params.shed_id !== undefined && params.shed_id !== 'All' && params.shed_id !== '') queryParams.set('shed_id', String(params.shed_id));
            }
            const url = `${API_ENDPOINTS.getAssetValues()}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
            const response = await farmvestApi.get(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

};
